import axios from 'axios';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      // Remove withCredentials - it's causing CORS issues
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', response.status, response.config.url);
        return response;
      },
      (error) => {
        console.error('❌ API Error:', error.message, error.config?.url);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(credentials) {
    try {
      const response = await this.api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async register(userData) {
    try {
      const response = await this.api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async logout() {
    try {
      const response = await this.api.post('/auth/logout');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getCurrentUser() {
    try {
      const response = await this.api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Category endpoints
  async getCategories() {
    try {
      const response = await this.api.get('/categories');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Vendor endpoints
  async getVendors(params = {}) {
    try {
      const response = await this.api.get('/vendors', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVendorById(id) {
    try {
      const response = await this.api.get(`/vendors/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createVendor(vendorData) {
    try {
      const response = await this.api.post('/vendors', vendorData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateVendor(id, vendorData) {
    try {
      const response = await this.api.put(`/vendors/${id}`, vendorData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteVendor(id) {
    try {
      const response = await this.api.delete(`/vendors/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Review endpoints
  async getReviews(vendorId) {
    try {
      const response = await this.api.get(`/reviews/vendor/${vendorId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createReview(reviewData) {
    try {
      const response = await this.api.post('/reviews/anonymous', reviewData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createAuthenticatedReview(vendorId, reviewData) {
    try {
      const response = await this.api.post(`/reviews/vendor/${vendorId}`, reviewData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Enquiry endpoints
  async createEnquiry(enquiryData) {
    try {
      const response = await this.api.post('/enquiries', enquiryData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getMyEnquiries() {
    try {
      const response = await this.api.get('/enquiries/my');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Search endpoints
  async searchVendors(query, filters = {}) {
    try {
      const params = { q: query, ...filters };
      const response = await this.api.get('/vendors/search', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Health check endpoint
  async healthCheck() {
    try {
      const response = await this.api.get('/health');
      console.log('✅ API Health Check:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API Health Check Failed:', error);
      throw this.handleError(error);
    }
  }

  // Error handling helper
  handleError(error) {
    console.log('🚨 API Error Details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL
      }
    });

    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'Server error occurred',
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error - please check your connection',
        status: 0,
        data: null,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null,
      };
    }
  }
}

const apiService = new ApiService();
export default apiService;